import { useMemo } from 'react';

import Badge from 'components/Badge/Badge';
import DropdownCombobox from 'components/Combobox';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { Currency, Method } from 'services/model';
import { styled } from 'styled-components';
import { getItemTextInvertedColor } from 'views/Widget/Widget.helper';

const Text = styled.span<{ $textColor?: string }>`
  text-transform: uppercase;
  color: ${({ $textColor }) => $textColor ?? 'unset'};
  white-space: nowrap;
`;

const Image = styled.img`
  width: 24px;
  height: 24px;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  padding: 12px 16px;
  gap: 8px;
`;

const SelectorsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DefaultItem = styled.span<{ $textColor?: string }>`
  padding: 12px 16px;
  font-size: 16px;
  line-height: 24px;
  color: ${({ $textColor }) => $textColor ?? 'unset'};
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
`;

const MethodItem = ({ item }: { item: Method }) => {
  const { t } = useTranslation();

  return (
    <Container style={{ justifyContent: 'space-between', width: '100%' }}>
      <Text>{`${item.network} ${item.token_standard ? `(${item.token_standard})` : ''}`}</Text>
      {item?.is_popular_choise && <Badge>{t('Popular choice')}</Badge>}
    </Container>
  );
};

const CurrencyItem = ({ item, textColor }: { item: Currency; textColor?: string }) => (
  <Container>
    <Image src={item.currency_icon} />
    <Text $textColor={textColor}>{item.currency}</Text>
  </Container>
);

interface CurrencySelectProps {
  currencies: Currency[];
  inputCurrencyValueChange: string;
  isFiat: boolean;
  isInvoiceCurrenciesFetching: boolean;
  onInputCurrencyValueChange: Dispatch<SetStateAction<string>>;
  selectedCurrency: Currency | null | undefined;
  setSelectedCurrency: Dispatch<SetStateAction<Currency | null | undefined>>;
  borderColor?: string;
  cashbackColor?: string;
  className?: string;
  inputMethodValueChange?: string;
  isInvoiceMethodsFetching?: boolean;
  isInvoiceMethodsInitialFetching?: boolean;
  isWidget?: boolean;
  label?: string;
  method?: Method;
  methods?: Method[];
  onChange?: (item: Method | null | undefined) => void;
  onInputMethodValueChange?: Dispatch<SetStateAction<string>>;
  openToTop?: boolean;
  setIsMethodsInitialFetching?: Dispatch<SetStateAction<boolean>>;
  textColor?: string;
}

const CurrencySelect = ({
  currencies,
  methods,
  method,
  isFiat,
  selectedCurrency,
  setSelectedCurrency,
  inputCurrencyValueChange,
  onInputCurrencyValueChange,
  inputMethodValueChange,
  onInputMethodValueChange,
  setIsMethodsInitialFetching,
  isInvoiceCurrenciesFetching,
  isInvoiceMethodsInitialFetching,
  isInvoiceMethodsFetching,
  label,
  onChange,
  openToTop,
  className,
  borderColor,
  textColor,
  isWidget,
}: CurrencySelectProps) => {
  const { t } = useTranslation();

  const checkIsDisabled = useMemo(() => {
    if (!selectedCurrency?.currency) return true;

    return isInvoiceMethodsInitialFetching || isInvoiceCurrenciesFetching;
  }, [isInvoiceMethodsInitialFetching, isInvoiceCurrenciesFetching, selectedCurrency?.currency]);

  const isNetworksFetching = isInvoiceMethodsFetching || false;

  return (
    <SelectorsWrapper>
      {label && <Label>{label}</Label>}
      {isFiat && (
        <DropdownCombobox
          borderColor={borderColor}
          className={className}
          inputValueChange={inputCurrencyValueChange}
          isFetching={isInvoiceCurrenciesFetching}
          isWidget={isWidget}
          items={currencies}
          itemToString={(item) => (item ? item.currency : '')}
          openToTop={openToTop}
          placeholder={t('Search') || ''}
          textColor={textColor}
          value={selectedCurrency}
          renderItem={(item) =>
            item?.currency ? (
              <CurrencyItem item={item} textColor={getItemTextInvertedColor()} />
            ) : (
              <DefaultItem>{t('Select currency')}</DefaultItem>
            )
          }
          onInputValueChange={onInputCurrencyValueChange}
          onChange={({ selectedItem }) => {
            setIsMethodsInitialFetching?.(true);
            setSelectedCurrency(selectedItem);
          }}
        />
      )}
      {onInputMethodValueChange && onChange && (
        <DropdownCombobox
          disabled={checkIsDisabled}
          inputValueChange={inputMethodValueChange || ''}
          isFetching={isNetworksFetching}
          isInitialFetching={isInvoiceMethodsInitialFetching}
          isShowFetchingInToggle={isInvoiceMethodsInitialFetching && isNetworksFetching}
          items={methods || []}
          itemToString={(item) => (item ? item.currency : '')}
          placeholder={t('Search') || ''}
          value={method}
          renderItem={(item) =>
            item?.currency ? <MethodItem item={item} /> : <DefaultItem>{t('Select network')}</DefaultItem>
          }
          onInputValueChange={onInputMethodValueChange}
          onChange={({ selectedItem }) => {
            onChange(selectedItem);
          }}
        />
      )}
    </SelectorsWrapper>
  );
};

export default CurrencySelect;
